/**
 * Command Parameter Model
 * Represents a single parameter for a command
 */
export class CommandParameter {
  constructor({
    name,
    type = 'string', // 'string', 'num', 'academicYear'
    displayName,
    value = '',
    required = true,
    placeholder = ''
  }) {
    this.name = name;
    this.type = type;
    this.displayName = displayName;
    this.value = value;
    this.required = required;
    this.placeholder = placeholder;
  }

  /**
   * Validates the parameter value based on its type
   */
  isValid() {
    if (this.required && (!this.value || this.value.toString().trim() === '')) {
      return false;
    }

    switch (this.type) {
      case 'num':
        return !this.value || !isNaN(this.value);
      case 'string':
      case 'academicYear':
        return true;
      default:
        return true;
    }
  }

  /**
   * Gets formatted value for display
   */
  getFormattedValue() {
    if (!this.value) return '';
    
    switch (this.type) {
      case 'num':
        return parseFloat(this.value).toString();
      default:
        return this.value.toString();
    }
  }
}

/**
 * Command Model
 * Represents a command with parameters and formatting capabilities
 */
export class Command {
  constructor({
    id,
    title,
    description,
    command, // Command template with {0}, {1}, etc. placeholders
    category,
    parameters = [], // Array of CommandParameter objects
    submitButtonText = 'Execute Command'
  }) {
    this.id = id;
    this.title = title;
    this.description = description;
    this.command = command;
    this.category = category;
    this.parameters = parameters.map(param => 
      param instanceof CommandParameter ? param : new CommandParameter(param)
    );
    this.submitButtonText = submitButtonText;
  }

  /**
   * Generates the final command by replacing parameter placeholders
   */
  generateCommand() {
    let finalCommand = this.command;
    
    this.parameters.forEach((param, index) => {
      const placeholder = `{${index}}`;
      const value = param.getFormattedValue();
      finalCommand = finalCommand.replace(placeholder, value);
    });

    return finalCommand;
  }

  /**
   * Validates all parameters
   */
  isValid() {
    return this.parameters.every(param => param.isValid());
  }

  /**
   * Gets all required parameters that are not filled
   */
  getInvalidParameters() {
    return this.parameters.filter(param => !param.isValid());
  }

  /**
   * Checks if command has parameters
   */
  hasParameters() {
    return this.parameters.length > 0;
  }

  /**
   * Resets all parameter values
   */
  resetParameters() {
    this.parameters.forEach(param => {
      param.value = '';
    });
  }

  /**
   * Creates a copy of the command for form usage
   */
  clone() {
    return new Command({
      id: this.id,
      title: this.title,
      description: this.description,
      command: this.command,
      category: this.category,
      parameters: this.parameters.map(param => new CommandParameter({
        name: param.name,
        type: param.type,
        displayName: param.displayName,
        value: param.value,
        required: param.required,
        placeholder: param.placeholder
      })),
      submitButtonText: this.submitButtonText
    });
  }
}

export default Command; 